import { useState } from "react";
import { cn } from "@/lib/utils";
import { Plus, Minus, HelpCircle } from "lucide-react";

const faqs = [
  {
    question: "Comment fonctionne la génération d'images avec Floop ?",
    answer: "Floop utilise une intelligence artificielle de pointe développée spécifiquement pour la création visuelle. Décrivez simplement votre image en français (ou anglais), choisissez un style et une résolution, puis cliquez sur Générer. En moins de 10 secondes, votre visuel est prêt à être téléchargé en HD ou 4K."
  },
  {
    question: "Qu'est-ce que Ad Creative Studio ?",
    answer: "Ad Creative Studio est notre nouvelle fonctionnalité pour créer des publicités professionnelles pour tous les réseaux sociaux (Instagram, Facebook, TikTok, Twitter, Pinterest, LinkedIn). Uploadez votre produit, choisissez un template et vos plateformes, et notre IA génère des créatives optimisées. Coût : 10 crédits par plateforme."
  },
  {
    question: "Quels formats et résolutions sont disponibles ?",
    answer: "Floop supporte les exports en PNG, JPG et WebP. Côté résolutions : Standard (1024x1024), Full HD (2048x2048), et 4K (4096x4096) selon votre plan. Vous pouvez aussi choisir parmi 9 formats d'image : carré, paysage 16:9, portrait 9:16, cinéma 21:9, Instagram 4:5, et plus encore."
  },
  {
    question: "Est-ce vraiment gratuit pour démarrer ?",
    answer: "Absolument ! Créez un compte en 30 secondes et recevez 10 crédits gratuits immédiatement. Aucune carte bancaire requise, aucun engagement. C'est suffisant pour tester Floop et créer vos premières images professionnelles."
  },
  {
    question: "À qui appartiennent les images générées ?",
    answer: "Toutes les images que vous créez avec Floop vous appartiennent à 100%. Vous pouvez les utiliser librement : réseaux sociaux, sites web, projets commerciaux, impressions, NFT... Aucune restriction, aucune attribution requise."
  },
  {
    question: "Quelle est la différence entre Floop et Floop Pro ?",
    answer: "Floop Standard est idéal pour la création rapide d'images à partir de texte. Floop Pro est notre modèle premium, optimisé pour la retouche photo professionnelle et les visuels ultra-détaillés. Il offre une qualité supérieure, parfaite pour les agences et créateurs exigeants."
  },
  {
    question: "Les crédits non utilisés expirent-ils ?",
    answer: "Avec nos plans payants (Starter, Pro, Studio Max), vos crédits non utilisés sont automatiquement reportés au mois suivant, sans limite de cumul. Vous ne perdez jamais ce que vous avez payé. Les crédits du plan gratuit sont renouvelés chaque mois."
  },
  {
    question: "Puis-je annuler mon abonnement à tout moment ?",
    answer: "Oui, sans conditions. Rendez-vous dans Paramètres > Abonnement et cliquez sur Annuler. Vos crédits restants seront utilisables jusqu'à la fin de votre période de facturation. Pas de frais cachés, pas de pénalités."
  },
  {
    question: "Proposez-vous une API pour les développeurs ?",
    answer: "Oui ! Notre API REST est disponible à partir du plan Pro. Elle vous permet d'intégrer la génération d'images Floop directement dans vos applications, workflows et outils. Documentation complète et support technique inclus."
  },
];

const FAQItem = ({ faq, index, isOpen, onToggle }: { 
  faq: typeof faqs[0]; 
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  return (
    <div 
      className={cn(
        "group rounded-2xl transition-all duration-300 overflow-hidden",
        "glass-card border",
        isOpen ? "border-primary/50 shadow-lg shadow-primary/10" : "border-white/10 hover:border-white/20"
      )}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-6 text-left"
      >
        <span className={cn(
          "font-semibold transition-colors duration-300 pr-4",
          isOpen ? "text-primary" : "text-foreground group-hover:text-primary"
        )}>
          {faq.question}
        </span>
        
        <div className={cn(
          "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300",
          isOpen 
            ? "bg-gradient-floop text-white rotate-0" 
            : "bg-white/5 text-muted-foreground group-hover:text-primary"
        )}>
          {isOpen ? (
            <Minus className="w-5 h-5" />
          ) : (
            <Plus className="w-5 h-5" />
          )}
        </div>
      </button>
      
      <div className={cn(
        "overflow-hidden transition-all duration-500",
        isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className="px-6 pb-6">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/30 to-transparent mb-4" />
          <p className="text-muted-foreground leading-relaxed">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-24 relative overflow-hidden" id="faq">
      {/* Background */}
      <div className="absolute inset-0 mesh-gradient-bg opacity-30" />
      <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-primary/10 blur-[150px] rounded-full" />
      <div className="absolute bottom-1/3 right-0 w-[400px] h-[400px] bg-accent/10 blur-[150px] rounded-full" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 glass-card border border-primary/30 rounded-full px-5 py-2.5 mb-6 text-sm font-medium text-foreground">
            <HelpCircle className="w-4 h-4 text-primary" />
            FAQ
          </span>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-display">
            Vos questions,{" "}
            <span className="gradient-text-animated">nos réponses</span>
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Tout ce que vous devez savoir pour créer vos premiers visuels IA avec Floop.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
